import {
  getConnections,
  getEntryNodes,
  getNodeMap,
  getOutgoingConnections,
  normalizeDefinition,
} from './mobileFlowBuilder';

const entryNodeIds = definition =>
  getEntryNodes(definition)
    .map(node => node.id)
    .filter(Boolean);

const reachableNodeIds = definition => {
  const entries = entryNodeIds(definition);
  const visited = new Set();
  const stack = [...entries];

  while (stack.length) {
    const nodeId = stack.pop();
    if (nodeId && !visited.has(nodeId)) {
      visited.add(nodeId);

      getOutgoingConnections(definition, nodeId).forEach(connection => {
        if (connection.target && !visited.has(connection.target)) {
          stack.push(connection.target);
        }
      });
    }
  }

  return visited;
};

const hasDisconnectedNodes = definition => {
  const nodes = normalizeDefinition(definition).nodes;
  const connectedIds = reachableNodeIds(definition);
  return nodes.some(node => !connectedIds.has(node.id));
};

const hasDuplicateNoMatchFlow = ({
  definition,
  flows,
  selectedInboxId,
  currentFlowId,
}) => {
  const hasNoMatch = getEntryNodes(definition).some(
    node => node.type === 'noMatch'
  );
  if (!hasNoMatch || !selectedInboxId) return false;

  return flows.some(existingFlow => {
    if (existingFlow.id === currentFlowId) return false;
    if (existingFlow.inbox_id !== selectedInboxId) return false;

    return (existingFlow.definition?.nodes || []).some(
      node => node.type === 'noMatch'
    );
  });
};

const outgoingByType = (definition, nodeId, sourceOutput) =>
  getOutgoingConnections(definition, nodeId).filter(
    connection => (connection.sourceOutput || 'next') === sourceOutput
  );

const validateInteractiveList = (definition, node, t) => {
  const listConnections = outgoingByType(definition, node.id, 'list');
  if (!listConnections.length) {
    return '';
  }

  const nodeMap = getNodeMap(definition);
  const keyboardNode = nodeMap[listConnections[0]?.target];

  if (!keyboardNode || keyboardNode.type !== 'keyboard') {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_BUTTON_REQUIRED');
  }

  const buttonText = keyboardNode.config?.buttonText?.trim() || '';
  if (!buttonText) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_BUTTON_REQUIRED');
  }

  if (buttonText.length > 20) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_BUTTON_LIMIT');
  }

  const sectionConnections = outgoingByType(
    definition,
    keyboardNode.id,
    'sections'
  );
  if (!sectionConnections.length) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.SECTION_ROWS_REQUIRED');
  }

  const totalRows = sectionConnections.reduce((count, sectionConnection) => {
    const sectionNode = nodeMap[sectionConnection.target];
    if (!sectionNode || sectionNode.type !== 'section') {
      return count;
    }

    return count + outgoingByType(definition, sectionNode.id, 'rows').length;
  }, 0);

  if (totalRows > 10) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_ROWS_LIMIT');
  }

  const sectionError = sectionConnections
    .map(sectionConnection => {
      const sectionNode = nodeMap[sectionConnection.target];
      if (!sectionNode || sectionNode.type !== 'section') {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.SECTION_ROWS_REQUIRED'
        );
      }

      const hasMultipleSections = sectionConnections.length > 1;
      const sectionTitle = sectionNode.config?.title?.trim() || '';
      if (hasMultipleSections && !sectionTitle) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_SECTION_TITLE_REQUIRED'
        );
      }

      if (sectionTitle.length > 24) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_SECTION_TITLE_LIMIT'
        );
      }

      const rowConnections = outgoingByType(definition, sectionNode.id, 'rows');
      if (!rowConnections.length) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.SECTION_ROWS_REQUIRED'
        );
      }

      if (rowConnections.length > 10) {
        return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.SECTION_ROWS_LIMIT');
      }

      return rowConnections
        .map(rowConnection => {
          const rowNode = nodeMap[rowConnection.target];
          if (!rowNode || rowNode.type !== 'row') {
            return t(
              'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_REQUIRED'
            );
          }

          const rowTitle = rowNode.config?.title?.trim() || '';
          const rowPayload = rowNode.config?.payload?.trim() || '';
          const rowDescription = rowNode.config?.description?.trim() || '';

          if (!rowTitle) {
            return t(
              'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_REQUIRED'
            );
          }

          if (rowTitle.length > 24) {
            return t(
              'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_LIMIT'
            );
          }

          if (rowDescription.length > 72) {
            return t(
              'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_DESCRIPTION_LIMIT'
            );
          }

          if (!rowPayload) {
            return t(
              'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_ID_REQUIRED'
            );
          }

          return '';
        })
        .find(Boolean);
    })
    .find(Boolean);

  if (sectionError) {
    return sectionError;
  }

  return '';
};

const validateNode = (definition, node, t) => {
  const config = node.config || {};

  if (node.type === 'interactive') {
    const buttonConnections = getOutgoingConnections(
      definition,
      node.id
    ).filter(connection => (connection.sourceOutput || 'next') === 'buttons');
    const listConnections = getOutgoingConnections(definition, node.id).filter(
      connection => (connection.sourceOutput || 'next') === 'list'
    );
    const hasMediaHeader =
      config.header?.enabled && config.header?.type === 'media';

    if (buttonConnections.length && listConnections.length) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_MODE_CONFLICT'
      );
    }

    if (hasMediaHeader && listConnections.length) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_MEDIA_LIST_CONFLICT'
      );
    }

    if (buttonConnections.length > 3) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BUTTON_LIMIT'
      );
    }

    const listValidationError = validateInteractiveList(definition, node, t);
    if (listValidationError) {
      return listValidationError;
    }
  }

  if (['start', 'noMatch', 'button', 'row'].includes(node.type)) {
    const nextConnections = getOutgoingConnections(definition, node.id, 'next');
    if (!nextConnections.length) {
      if (node.type === 'start') {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.START_ROUTE_REQUIRED'
        );
      }
      if (node.type === 'noMatch') {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.NO_MATCH_ROUTE_REQUIRED'
        );
      }
      if (node.type === 'button') {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_ROUTE_REQUIRED'
        );
      }
      if (node.type === 'row') {
        return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_ROUTE_REQUIRED');
      }
    }
  }

  switch (node.type) {
    case 'start':
      if (!config.title?.trim()) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.START_TITLE_REQUIRED'
        );
      }
      return '';
    case 'text':
      return config.body?.trim()
        ? ''
        : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEXT_BODY_REQUIRED');
    case 'interactive':
      if (!config.body?.trim()) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BODY_REQUIRED'
        );
      }
      if (config.body.length > 1024) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BODY_REQUIRED'
        );
      }
      if ((config.footer || '').length > 60) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BODY_REQUIRED'
        );
      }
      return '';
    case 'button':
      if (!config.title?.trim()) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_TITLE_REQUIRED'
        );
      }
      return config.title.length <= 20
        ? ''
        : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_TITLE_REQUIRED');
    case 'keyboard':
      if (!config.body?.trim()) {
        return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEXT_BODY_REQUIRED');
      }
      if (!config.buttonText?.trim()) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_BUTTON_REQUIRED'
        );
      }
      return config.buttonText.trim().length <= 20
        ? ''
        : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_BUTTON_LIMIT');
    case 'section':
      return config.title?.trim()
        ? ''
        : t(
            'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LIST_SECTION_TITLE_REQUIRED'
          );
    case 'row':
      if (!config.title?.trim()) {
        return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_REQUIRED');
      }
      if (config.title.trim().length > 24) {
        return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_LIMIT');
      }
      if (!config.description?.trim()) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_DESCRIPTION_LIMIT'
        );
      }
      if ((config.description || '').trim().length > 72) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_DESCRIPTION_LIMIT'
        );
      }
      return '';
    case 'carousel':
      if (!config.body?.trim()) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_BODY_REQUIRED'
        );
      }
      if ((config.cards || []).length < 2) {
        return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_CARD_MIN');
      }
      if (
        (config.cards || []).some(
          card =>
            !card.body?.trim() ||
            !(card.mediaUrl || card.mediaLink || '').trim() ||
            !card.buttonText?.trim() ||
            !card.buttonUrl?.trim()
        )
      ) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_BODY_REQUIRED'
        );
      }
      return '';
    case 'locationRequest':
      return config.body?.trim()
        ? ''
        : t(
            'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_REQUEST_BODY_REQUIRED'
          );
    case 'contact':
      return config.formattedName?.trim() && config.phoneNumber?.trim()
        ? ''
        : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CONTACT_REQUIRED_FIELDS');
    case 'ai':
      return config.instructions?.trim() && config.fallbackMessage?.trim()
        ? ''
        : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEXT_BODY_REQUIRED');
    default:
      return '';
  }
};

export const validateMobileFlow = ({
  definition,
  flowName,
  selectedInboxId,
  flows,
  currentFlowId,
  t,
}) => {
  const normalized = normalizeDefinition(definition);
  const entries = getEntryNodes(normalized);
  const normalizedFlowName = flowName?.trim()?.toLowerCase?.() || '';

  if (!selectedInboxId) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INBOX_REQUIRED');
  }

  if (
    normalizedFlowName &&
    flows.some(existingFlow => {
      if (existingFlow.id === currentFlowId) return false;
      if (existingFlow.inbox_id !== selectedInboxId) return false;
      if (existingFlow.status === 'archived') return false;

      return (
        (existingFlow.name || '').trim().toLowerCase() === normalizedFlowName
      );
    })
  ) {
    return t('MOBILE_CONVERSATION_FLOWS.EDITOR.DUPLICATE_NAME');
  }

  if (entries.length !== 1) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ENTRY_REQUIRED');
  }

  if (
    hasDuplicateNoMatchFlow({
      definition: normalized,
      flows,
      selectedInboxId,
      currentFlowId,
    })
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.NO_MATCH_UNIQUE');
  }

  if (hasDisconnectedNodes(normalized)) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.DISCONNECTED_NODES');
  }

  const nodeMap = getNodeMap(normalized);
  const invalidConnection = getConnections(normalized).find(
    connection => !nodeMap[connection.source] || !nodeMap[connection.target]
  );

  if (invalidConnection) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.DISCONNECTED_NODES');
  }

  const invalidNode = normalized.nodes.find(node =>
    Boolean(validateNode(normalized, node, t))
  );

  return invalidNode ? validateNode(normalized, invalidNode, t) : '';
};
